import React, { useState } from "react";
import {
  View,
  TextInput,
  Text,
  TouchableOpacity,
  Platform,
  Alert,
  ActivityIndicator,
} from "react-native";
import ActionSheetIOS from "../../utils/helpers/ActionSheetIOS";
import { Picker } from "@react-native-picker/picker";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { buildingListActions } from "../../store/building-slice";
import { firebaseApp } from "../../firebase/config";
import { getFirestore, addDoc, doc, collection } from "firebase/firestore";
import styles from "./styles";
import { executeFirestoreWriteWithQueue } from "../../utils/offlineWriteQueue";

import { useSelector, useDispatch } from "react-redux";
import SectionCard from "../../components/SectionCard";
import SelectField from "../../components/SelectField";
import TextAreaField from "../../components/TextAreaField";
import EmptyState from "../../components/EmptyState";

const LOCATION_OPTIONS = ["Roof Top", "Working Position"];
const WEATHER_CONDITION_OPTIONS = [
  "Acceptable",
  "Heavy Rain",
  "Strong Winds",
  "Storm/Thunderstorm",
  "Snow / Freezing Temp",
];

const WeatherForm = ({ route, navigation }) => {
  const db = getFirestore(firebaseApp);
  const dispatch = useDispatch();
  const [location, setLocation] = useState("");
  const [speed, setSpeed] = useState("");
  const [condition, setCondition] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const date = new Date();
  const dateString = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  const { siteId } = route.params;

  const currentClean = useSelector((state) => state.buildingList.currentClean);
  const userUid = useSelector((state) => state.auth.uid);
  const selectedBuilding = useSelector((state) => state.buildingList.selectedBuilding);
  const siteIsAdhoc = selectedBuilding?.isAdhoc || false;
  const buildingName = siteIsAdhoc ? selectedBuilding?.site_name : siteId;

  if (!siteId) {
    return (
      <EmptyState
        title="Missing site"
        message="This weather report cannot be submitted because the site is not available."
        actionLabel="Back"
        onAction={() => navigation.goBack()}
      />
    );
  }

  const siteRef = doc(db, siteIsAdhoc ? "adhocSites" : "BuildingsX", siteId);

  const isValid = location !== "" && condition !== "" && speed.trim() !== "";

  const submitFormHandler = async () => {
    if (!isValid) {
      setError("Please complete location, wind speed and weather condition.");
      return;
    }

    setError("");
    setSubmitting(true);

    const documentToAdd = {
      comments: comment.trim(),
      date,
      weatherCondition: condition,
      windMeasurementLocation: location,
      windSpeed: speed.trim(),
      type: "Weather Report",
      completedBy: userUid,
      cleanId: currentClean,
    };

    try {
      const { queued } = await executeFirestoreWriteWithQueue({
        db,
        queueLabel: "weather-form",
        writeOperation: async () => {
          await addDoc(collection(siteRef, "windowCleaningForms"), documentToAdd);
        },
      });
      dispatch(buildingListActions.setWeatherForm(documentToAdd));

      const alertMessage = queued
        ? "Offline: weather report saved locally and queued for sync."
        : "Thanks, weather report submitted successfully.";
      if (Platform.OS === "web") {
        alert(alertMessage);
        navigation.navigate("Building", { name: buildingName });
      } else {
        Alert.alert("Thanks", alertMessage, [
          { text: "OK", onPress: () => navigation.navigate("Building", { name: buildingName }) },
        ]);
      }
    } catch (submitError) {
      const message = submitError?.message || "Unable to submit the weather report. Please try again.";
      if (Platform.OS === "web") alert(message);
      else Alert.alert("Oops", message);
    } finally {
      setSubmitting(false);
    }
  };

  const openLocationOptionsOnIos = () => {
    if (!ActionSheetIOS) {
      return Alert.alert(
        "Unsupported",
        "Action sheets are only supported on iOS in this web build."
      );
    }

    ActionSheetIOS.showActionSheetWithOptions(
      {
        options: ["Cancel", ...LOCATION_OPTIONS],
        cancelButtonIndex: 0,
        userInterfaceStyle: "dark",
      },
      (buttonIndex) => {
        if (buttonIndex === 0) {
          return setLocation("");
        }
        setLocation(LOCATION_OPTIONS[buttonIndex - 1]);
      }
    );
  };

  const openConditionOptionsOnIos = () => {
    if (!ActionSheetIOS) {
      return Alert.alert(
        "Unsupported",
        "Action sheets are only supported on iOS in this web build."
      );
    }

    ActionSheetIOS.showActionSheetWithOptions(
      {
        options: ["Cancel", ...WEATHER_CONDITION_OPTIONS],
        cancelButtonIndex: 0,
        userInterfaceStyle: "dark",
      },
      (buttonIndex) => {
        if (buttonIndex === 0) {
          return setCondition("");
        }
        setCondition(WEATHER_CONDITION_OPTIONS[buttonIndex - 1]);
      }
    );
  };

  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{ flex: 1 }}
      enableOnAndroid={true}
      automaticallyAdjustContentInsets={true}
      enableAutomaticScroll={Platform.OS === "ios"}
    >
      <View style={styles.root}>
        <Text style={styles.pageTitle}>Weather Report</Text>
        <Text style={styles.pageSubtitle}>Log wind speed, weather condition and notes to keep the team safe.</Text>

        <SectionCard title="Measurement details" subtitle="Record the location and conditions before continuing.">
          {Platform.OS === "ios" ? (
            <TouchableOpacity onPress={openLocationOptionsOnIos} style={styles.iosButton}>
              <Text style={styles.iosButtonText}>{location || "Select location"}</Text>
            </TouchableOpacity>
          ) : (
            <SelectField
              label="Wind measurement location"
              value={location}
              options={LOCATION_OPTIONS.map((option) => ({ label: option, value: option }))}
              onValueChange={setLocation}
              placeholder="Select location"
              helperText="Where was the wind measurement taken?"
              error={!location && error ? "Location is required." : undefined}
            />
          )}

          <TextInput
            style={[styles.input, !speed && error ? styles.errorInput : null]}
            placeholder="Wind speed (mph)"
            placeholderTextColor="#94a3b8"
            value={speed}
            keyboardType="numeric"
            returnKeyType="done"
            onChangeText={(value) => setSpeed(value.replace(/[^0-9]/g, ""))}
          />
          <Text style={[styles.helperText, !speed && error ? styles.errorText : null]}>
            {(!speed && error && "Wind speed is required.") || "Enter the recorded wind speed."}
          </Text>

          {Platform.OS === "ios" ? (
            <TouchableOpacity onPress={openConditionOptionsOnIos} style={styles.iosButton}>
              <Text style={styles.iosButtonText}>{condition || "Select weather condition"}</Text>
            </TouchableOpacity>
          ) : (
            <SelectField
              label="Weather condition"
              value={condition}
              options={WEATHER_CONDITION_OPTIONS.map((option) => ({ label: option, value: option }))}
              onValueChange={setCondition}
              placeholder="Select weather condition"
              helperText="Select the condition that best matches this site."
              error={!condition && error ? "Condition is required." : undefined}
            />
          )}
        </SectionCard>

        <SectionCard title="Notes" subtitle="Optional detail for the handover record.">
          <TextAreaField
            label="Comments"
            value={comment}
            onChangeText={setComment}
            placeholder="Add any observations or clarifications..."
            helperText="Optional note to explain the site condition."
          />
        </SectionCard>

        {!!error && <Text style={styles.formError}>{error}</Text>}

        <TouchableOpacity
          onPress={submitFormHandler}
          style={[styles.submitButton, { backgroundColor: isValid ? "#2563eb" : "#9ca3af" }]}
          disabled={!isValid || submitting}
          activeOpacity={0.85}
        >
          {submitting ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.submitButtonText}>Submit weather report</Text>
          )}
        </TouchableOpacity>
      </View>
    </KeyboardAwareScrollView>
  );
};

export default WeatherForm;
